import { Component, OnInit } from '@angular/core';
import { ResultModalRedeem, StatusBag, TypeLoading, RedeemMethod, BENEFIT_TYPE } from '@app/enums';
import { TypesErrors } from '@app/enums/errors.enum';
import { Passenger, Reservation } from '@app/models/micro';
import { LoadingService } from '@app/services/loading.service';
import { MessagesService } from '@app/services/messages.service';
import { ApiAncillaries } from '@app/services/micro/ancillaries.service';
import { GlobalsService } from '@app/services/micro/globals.service';
import { messageParamsGeneric } from '@app/utils';
import { classCssTiers, getCobrandLabel, getCobrandLogo } from '@app/utils/constants/redentions';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-bags-redemption-modal',
  templateUrl: './bags-redemption-modal.component.html',
  styleUrls: ['./bags-redemption-modal.component.scss'],
  standalone: false,
})
export class BagsRedemptionModalComponent implements OnInit {
  passenger: Passenger;
  reservation: Reservation;
  redemptionBenefitType: 'checkedBag' | 'carryOn' = 'checkedBag';
  showLoading: boolean = false;
  submitRedeem: boolean = false;
  radioAnswer: boolean = false;
  selectedMethod: RedeemMethod | null = null;

  constructor(
    public modal: NgbActiveModal,
    private readonly ancillaries: ApiAncillaries,
    private readonly globalService: GlobalsService,
    private readonly messageService: MessagesService,
    private readonly loadingService: LoadingService
  ) {}

  ngOnInit() {
    if (
      this.passenger.typeCorporate === 'corporate' &&
      !this.passenger.typeTier &&
      !this.passenger.typeCobrand
    ) {
      this.selectedMethod = 'corporate';
    } else if (
      this.passenger.typeTier === 'tier' &&
      !this.passenger.typeCobrand &&
      !this.passenger.typeCorporate
    ) {
      this.selectedMethod = 'tier';
    } else if (
      this.passenger.typeCobrand === 'cobrand' &&
      !this.passenger.typeTier &&
      !this.passenger.typeCorporate
    ) {
      this.selectedMethod = 'cobrand';
    } else if (
      this.redemptionBenefitType === 'carryOn' &&
      this.passenger.typeCobrandCarryOn === 'cobrand'
    ) {
      this.selectedMethod = 'cobrand';
    }

    this.loadingService.loadings[TypeLoading.COMPONENT_LOADING].loading$.subscribe(
      (loading: boolean) => {
        if (loading) {
          this.showLoading = true;
        }
      }
    );
  }

  get classTier() {
    return classCssTiers[this.passenger.benefits?.tier] || '';
  }

  get cobrandLabel(): string {
    return getCobrandLabel(this.passenger?.benefits?.cobrandCode || '');
  }

  get cobrandLogo(): string {
    return getCobrandLogo(this.passenger?.benefits?.cobrandCode || '');
  }

  get existsAncillaries() {
    return this.passenger.bagOfferStack?.length > 0;
  }

  redeemBag(passenger: Passenger) {
    this.submitRedeem = true;
    const isCarryOn = this.redemptionBenefitType === 'carryOn';
    const extraBags = isCarryOn
      ? passenger.bagOfferCarryOnStack?.find((bag) => bag.order == passenger.bagStackCarryon.length)
      : passenger.bagOfferStack?.find((bag) => bag.order == passenger.bagStack.length);

    
    const hasCarryOnIncluded = passenger.bagStackCarryon?.some(
      (b) => (b.status === StatusBag.INCLUDE || b.status === StatusBag.PAID) && !b.used && !b.isRedemptionCobrand && !b.isRedemptionTier && !b.isRedemptionFF
    );
    const carryOnRedeemable = !isCarryOn && this.selectedMethod === 'cobrand' && !hasCarryOnIncluded
      ? passenger.bagStackCarryon?.find((b) => b.status === StatusBag.REDEEMED)
      : null;
    const extraCarryOn = carryOnRedeemable
      ? passenger.bagOfferCarryOnStack?.[0] || carryOnRedeemable
      : null;

    const ancillariesToRedeem = extraCarryOn && extraBags
      ? [extraBags, extraCarryOn]
      : extraBags;

    this.ancillaries
      .redeemAncillaries(this.reservation, passenger, ancillariesToRedeem, this.selectedMethod)
      .subscribe({
        next: (res) => {
          const stack = isCarryOn ? passenger.bagStackCarryon : passenger.bagStack;
          let bag = stack.find((b) => b.status == StatusBag.REDEEMED);
          const bagRedeemed = res?.travelerInfo[0]?.ancillaries?.[0]?.ancillary;
          if (bag) {
            if (bagRedeemed) Object.assign(bag, bagRedeemed);
            bag.status = StatusBag.REDEEMED_APPLIED;
            bag.isRedemptionFF = false;
            bag.isRedemptionTier = this.selectedMethod === BENEFIT_TYPE.TIER;
            bag.isRedemptionCobrand = this.selectedMethod === BENEFIT_TYPE.COBRAND;
            bag.isRedemptionCorporate = this.selectedMethod === BENEFIT_TYPE.CORPORATE;
            bag.selected = true;
          }
          if (carryOnRedeemable) {
            const carryOnRedeemed = res?.travelerInfo[0]?.ancillaries?.[1]?.ancillary;
            if (carryOnRedeemed) Object.assign(carryOnRedeemable, carryOnRedeemed);
            carryOnRedeemable.status = StatusBag.REDEEMED_APPLIED;
            carryOnRedeemable.isRedemptionCobrand = true;
            carryOnRedeemable.selected = true;
            if (passenger.bagStackCarryon) {
              passenger.bagStackCarryon = passenger.bagStackCarryon.filter(
                (bag) => bag.status !== StatusBag.TO_PAY
              );
            }
            if (passenger.bagOfferCarryOnStack) {
              passenger.bagOfferCarryOnStack.forEach((offerBag) => {
                if (offerBag.status === StatusBag.TO_PAY) {
                  offerBag.status = StatusBag.NONE;
                  offerBag.selected = false;
                }
              });
            }
          }
          this.globalService.setPassenger(passenger);
          this.modal.close({
            result: ResultModalRedeem.SUCCESS,
            selectedMethod: this.selectedMethod,
          });
          this.submitRedeem = false;
        },
        error: () => {
          this.submitRedeem = false;
          this.modal.close(ResultModalRedeem.ERROR);
          this.messageService.error(
            { typesError: TypesErrors.WERE_SORRY_GENERIC },
            null,
            TypesErrors.WERE_SORRY_GENERIC,
            messageParamsGeneric
          );
        },
      });
  }

  onClose() {
    this.modal.close(ResultModalRedeem.NO_REDEEM);
  }

  onRedeem() {
    if (this.submitRedeem) return;
    this.redeemBag(this.passenger);
  }
}
